/**
 * BettingPanel — fixed bottom bar with bet presets and bet action buttons.
 * In a round user can bet either solo or squad (team), never both.
 */

import { useState, useEffect, useMemo } from "react"
import { motion } from "framer-motion"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"

import SettingsIcon from "@/assets/icons/settings-icon.svg?react"
import TonIcon from "@/assets/icons/ton-icon.svg?react"

import {
  BetSettingsSheet,
  hasBetSettingsInStorage,
  loadBetSettings,
  persistBetSettings,
  type BetSettings,
} from "./BetSettingsSheet"

import { formatHundredthsTrimZeros } from "@/shared/lib/pvpRouletteMath"
import {
  AnalyticsEvent,
  analyticsErrorMessage,
  trackAnalyticsEvent,
} from "@/shared/lib/analytics"

import {
  usePvpRoomActionsAllowed,
  usePvpRoomParties,
  usePvpRoomPhase,
  usePvpRoomRoundInfo,
  usePvpRoomMyBetType,
} from "../hooks/usePvpRoomSelectors"

import {
  usePvpRoomsStore,
  usePvpRoomOptimisticPending,
} from "@/entities/pvp-roulette"
import {
  filterBettableGifts,
  giftKeys,
  INVENTORY_GIFTS_PARAMS,
  useUserGifts,
} from "@/entities/gift"
import { useBalance, userKeys } from "@/entities/user/hooks"
import type { Balance } from "@/shared/api/types"

import { DepositSheet } from "@/features/wallet"

import { placeBet } from "@/shared/api"
import { getUserIdFromToken } from "@/shared/api/auth"
import { DEFAULT_GROUP_ID } from "@/shared/constants/pvpRoulette"
import { useDemoModeStore, useNetworkStore } from "@/shared/store"
import { translatePath, useI18n } from "@/shared/i18n"
import { useFeatureFlag } from "@/shared/features"
import { makeErrorToast } from "@/shared/lib/toast-error"
import { telegram } from "@/shared/lib/telegram"
import { getGiftRelayBotTelegramUrl } from "@/features/inventory"
import type {
  BetUserData,
  RoundParty,
  RoundSnapshot,
  SnapshotBet,
} from "@/shared/api/types/pvpRoulette"
import LargeButton from "@/shared/ui/LargeButton"
import IconButton from "@/shared/ui/IconButton"
import PresetButton from "@/shared/ui/PresetButton"
import ShortButton from "@/shared/ui/ShortButton"

import { PrimaryButtonL } from "@/components/ui/primary-button-l"

import { cn } from "@/shared/lib/utils"

import { BetAssetToggle, type BetAsset } from "./BetAssetToggle"
import { GiftPickerSheet } from "./GiftPickerSheet"
import { GiftPresetRow } from "./GiftPresetRow"

const MotionPrimaryButtonL = motion(PrimaryButtonL)

export const BettingPanel = ({
  roomId,
  currentUserId,
}: {
  roomId: string
  currentUserId?: string | null
}) => {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const giftsEnabled = useFeatureFlag("gifts")

  const userId = currentUserId ?? getUserIdFromToken()

  const [isDepositOpen, setIsDepositOpen] = useState(false)
  const [betAsset, setBetAsset] = useState<BetAsset>("TON")
  const [isGiftPickerOpen, setIsGiftPickerOpen] = useState(false)
  const effectiveBetAsset: BetAsset = giftsEnabled ? betAsset : "TON"

  const giftsCache = useUserGifts(INVENTORY_GIFTS_PARAMS, { enabled: giftsEnabled })
  const bettableGifts = useMemo(
    () => filterBettableGifts(giftsCache.data?.items ?? []),
    [giftsCache.data]
  )
  const hasGifts: boolean | undefined = giftsCache.data
    ? bettableGifts.length > 0
    : undefined

  const { data: balanceData, isFetched: isBalanceFetched } = useBalance()

  const balanceTon = Number(balanceData?.balance?.amount ?? "0")
  const maxAmount =
    Number.isFinite(balanceTon) && balanceTon > 0 ? balanceTon : 0
  const needsDepositFlow = maxAmount <= 0

  const [settings, setSettings] = useState<BetSettings>(() =>
    loadBetSettings(balanceData?.balance?.amount)
  )
  const [sheetOpen, setSheetOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const phase = usePvpRoomPhase(roomId)
  const actions = usePvpRoomActionsAllowed(roomId)
  const pending = usePvpRoomOptimisticPending(roomId)
  const roundInfo = usePvpRoomRoundInfo(roomId)
  const parties = usePvpRoomParties(roomId)
  const myBetType = usePvpRoomMyBetType(roomId, userId)
  const placeBetSolo = usePvpRoomsStore((s) => s.placeBetSolo)
  const ingestRoundSnapshot = usePvpRoomsStore((s) => s.ingestRoundSnapshot)
  const clearOptimisticPending = usePvpRoomsStore(
    (s) => s.clearOptimisticPending
  )
  const isOnline = useNetworkStore((s) => s.isOnline)

  const canBet = (actions?.canPlaceBet ?? false) && !isSubmitting && isOnline
  const allInValue = maxAmount
  const amountsClose = (a: number, b: number) => Math.abs(a - b) < 0.001
  const isBalancePending = !isBalanceFetched && allInValue <= 0

  /** One highlighted control: matching preset pill, else all-in if amount === balance, else settings */
  const presetMatchIndex = settings.presets.findIndex((p) =>
    amountsClose(settings.amount, p)
  )
  const matchesPreset = presetMatchIndex >= 0
  const matchesAllIn =
    allInValue > 0 && amountsClose(settings.amount, allInValue)
  const activePresetIndex = matchesPreset ? presetMatchIndex : null
  const allInButtonActive = !matchesPreset && matchesAllIn
  const shouldHighlightSettings = !matchesPreset && !matchesAllIn

  useEffect(() => {
    const loaded = loadBetSettings(balanceData?.balance?.amount)
    setSettings((prev) => {
      if (isBalancePending) return prev

      const hasStoredSettings = hasBetSettingsInStorage()
      const nextAmount = resolveNextAmount({
        allInValue,
        hasStoredSettings,
        prevAmount: prev.amount,
        loadedAmount: loaded.amount,
      })

      return {
        ...(hasStoredSettings ? prev : loaded),
        amount: nextAmount,
      }
    })
  }, [allInValue, balanceData?.balance?.amount, isBalancePending])

  useEffect(() => {
    if (!pending) return
    const expiresAt = pending.submittedAt + pending.ttlMs
    const delayMs = Math.max(0, expiresAt - Date.now())
    const timer = window.setTimeout(() => {
      clearOptimisticPending(roomId)
    }, delayMs)
    return () => window.clearTimeout(timer)
  }, [clearOptimisticPending, pending, roomId])

  // Hide only during SPINNING/TRICKS/SLOWING (TricksPanel shows instead)
  if (phase === "SPINNING" || phase === "TRICKS" || phase === "SLOWING")
    return null

  const applyAmount = (
    amount: number,
    options?: { preservePrecision?: boolean }
  ) => {
    const normalized = options?.preservePrecision
      ? amount
      : Math.round(amount * 10) / 10
    const nextAmount = Math.max(0, Math.min(normalized, allInValue))
    const next = { ...settings, amount: nextAmount }
    setSettings(next)
    persistBetSettings(next)
  }

  const executePlaceBet = async (
    mode: "solo" | "squad",
    stakeAmount: number
  ): Promise<boolean> => {
    if (!canBet || !roundInfo?.roundId || stakeAmount <= 0) return false
    const uid = currentUserId ?? getUserIdFromToken()
    if (!uid) return false

    const amountNano = Math.round(stakeAmount * 1e9)
    const isTeam = mode === "squad"
    const partyId = isTeam ? DEFAULT_GROUP_ID : `solo:${uid}`
    const partyType = isTeam ? "TEAM" : "SOLO"
    const isAddStake = myBetType != null
    const roundId = roundInfo.roundId

    placeBetSolo(roomId, {
      amount: stakeAmount,
      currency: "ton",
      correlationId: `bet_${Date.now()}`,
    })
    setIsSubmitting(true)
    try {
      if (useDemoModeStore.getState().active) {
        const currentRawRound = usePvpRoomsStore.getState().rooms[roomId]?.rawRound
        if (currentRawRound) {
          const nextRawRound = appendDemoBetToSnapshot(currentRawRound, {
            userId: uid,
            amountTon: stakeAmount,
            mode,
            nowMs: Date.now(),
          })
          ingestRoundSnapshot(roomId, nextRawRound, {
            userId: uid,
            nowMs: Date.now(),
          })
        }
        queryClient.setQueryData<Balance>(userKeys.balance(), (old) => {
          const prev = Number(old?.balance?.amount ?? "0")
          const next = Math.max(0, prev - stakeAmount)
          return { balance: { amount: next.toString() } }
        })
        trackAnalyticsEvent(AnalyticsEvent.BetPlaced, {
          bet_mode: mode,
          asset_class: "TON",
          amount_ton: stakeAmount,
          round_id: roundId,
          is_add_stake: isAddStake,
        })
        return true
      }
      await placeBet({
        round_id: roundId,
        party_id: partyId,
        party_type: partyType,
        asset_class: "TON",
        amount: amountNano,
      })
      trackAnalyticsEvent(AnalyticsEvent.BetPlaced, {
        bet_mode: mode,
        asset_class: "TON",
        amount_ton: stakeAmount,
        round_id: roundId,
        is_add_stake: isAddStake,
      })
      return true
    } catch (err) {
      console.error("[BettingPanel] placeBet failed:", err)
      trackAnalyticsEvent(AnalyticsEvent.BetFailed, {
        bet_mode: mode,
        asset_class: "TON",
        round_id: roundId,
        amount_ton: stakeAmount,
        is_partial_failure: false,
        error_message: analyticsErrorMessage(err),
      })
      clearOptimisticPending(roomId)
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePlaceBet = async (mode: "solo" | "squad") => {
    await executePlaceBet(mode, settings.amount)
  }

  const handlePlaceFromSheet = async ({
    settings: next,
    mode,
  }: {
    settings: BetSettings
    mode: "solo" | "squad"
  }) => {
    setSettings(next)
    persistBetSettings(next)
    const ok = await executePlaceBet(mode, next.amount)
    if (ok) setSheetOpen(false)
  }

  const lockedMode: "solo" | "squad" | null =
    myBetType === "team" ? "squad" : myBetType

  const handleConfirmGiftBets = async (giftIds: string[]) => {
    if (!roundInfo?.roundId || giftIds.length === 0) return
    const uid = currentUserId ?? getUserIdFromToken()
    if (!uid) return

    const mode = lockedMode ?? "solo"
    const isTeam = mode === "squad"
    const partyId = isTeam ? DEFAULT_GROUP_ID : `solo:${uid}`
    const partyType: "SOLO" | "TEAM" = isTeam ? "TEAM" : "SOLO"

    setIsSubmitting(true)
    try {
      const results = await Promise.allSettled(
        giftIds.map((nftId) =>
          placeBet({
            round_id: roundInfo.roundId,
            party_id: partyId,
            party_type: partyType,
            asset_class: "TELEGRAM_GIFT",
            amount: null,
            nft_id: nftId,
          })
        )
      )
      const failed = results.filter((r) => r.status === "rejected")
      const succeeded = results.length - failed.length

      if (failed.length > 0) {
        console.error(
          `[BettingPanel] gift placeBet: ${failed.length}/${giftIds.length} failed`,
          failed
        )
        const firstError = failed.find(
          (r): r is PromiseRejectedResult => r.status === "rejected"
        )?.reason
        trackAnalyticsEvent(AnalyticsEvent.BetFailed, {
          bet_mode: mode,
          asset_class: "TELEGRAM_GIFT",
          round_id: roundInfo.roundId,
          gift_count: giftIds.length,
          is_partial_failure: succeeded > 0,
          error_message: analyticsErrorMessage(firstError),
        })
        makeErrorToast({
          label:
            succeeded > 0
              ? translatePath("betting.giftBetPartialFailed", {
                  failed: failed.length,
                  total: giftIds.length,
                })
              : translatePath("betting.giftBetFailed"),
        })
      }

      if (succeeded > 0) {
        trackAnalyticsEvent(AnalyticsEvent.BetPlaced, {
          bet_mode: mode,
          asset_class: "TELEGRAM_GIFT",
          gift_count: succeeded,
          round_id: roundInfo.roundId,
          is_add_stake: myBetType != null,
        })
        void queryClient.invalidateQueries({ queryKey: giftKeys.all })
        setIsGiftPickerOpen(false)
      }
    } finally {
      setIsSubmitting(false)
    }
  }
  const previewMode: "solo" | "squad" = lockedMode ?? "solo"
  const formatPresetLabel = (amount: number) => formatHundredthsTrimZeros(amount)
  const parseAmount = (value: string | undefined) => {
    const parsed = Number(value ?? "0")
    return Number.isFinite(parsed) ? parsed : 0
  }
  const totalBankTon = parties.reduce(
    (sum, party) => sum + parseAmount(party.betAmount),
    0
  )
  const resolveCurrentPartyBankTon = (mode: "solo" | "squad") => {
    if (!userId) return 0
    if (mode === "squad") {
      const teamParty = parties.find(
        (p) =>
          p.partyType === "TEAM" ||
          p.partyId === DEFAULT_GROUP_ID ||
          p.userId === DEFAULT_GROUP_ID
      )
      return parseAmount(teamParty?.betAmount)
    }
    const soloParty = parties.find((p) => {
      if (p.partyType === "TEAM") return false
      if (p.userId === userId) return true
      return p.partyId === `solo:${userId}`
    })
    return parseAmount(soloParty?.betAmount)
  }
  const currentPartyBankTon = resolveCurrentPartyBankTon(previewMode)
  const sectorNowPercent =
    totalBankTon > 0 ? (currentPartyBankTon / totalBankTon) * 100 : 0
  const sectorAfterPercent =
    settings.amount > 0 && totalBankTon + settings.amount > 0
      ? ((currentPartyBankTon + settings.amount) /
          (totalBankTon + settings.amount)) *
        100
      : sectorNowPercent

  const amt = formatPresetLabel(settings.amount)

  const assetToggle = giftsEnabled ? (
    <BetAssetToggle
      value={betAsset}
      onChange={setBetAsset}
      ariaLabels={{
        ton: t("betting.assetTon"),
        gift: t("betting.assetGift"),
      }}
      className="absolute -top-11 right-4"
    />
  ) : null

  const giftsList = bettableGifts
  const placeGiftBets = (giftIds: string[]) => {
    void handleConfirmGiftBets(giftIds)
  }
  const giftPresetRow = hasGifts ? (
    <GiftPresetRow
      gifts={giftsList}
      disabled={isSubmitting || !!pending}
      onPlace={placeGiftBets}
    />
  ) : null

  if (needsDepositFlow) {
    return (
      <>
        <div className="relative flex flex-col gap-3 px-4 pb-3 pt-4">
          {assetToggle}
          {effectiveBetAsset === "GIFT" ? (
            <>
              {giftPresetRow}
              <GiftModeCta
                hasGifts={hasGifts}
                selectGiftsLabel={t("betting.selectGifts")}
                onSelectGifts={() => setIsGiftPickerOpen(true)}
              />
            </>
          ) : (
            <LargeButton
              variant="green"
              onClick={() => setIsDepositOpen(true)}
            >
              {t("betting.depositTon")}
            </LargeButton>
          )}
        </div>
        <DepositSheet
          isOpen={isDepositOpen}
          setIsOpen={setIsDepositOpen}
          depositUiSource="pvp_betting_panel"
        />
        <GiftPickerSheet
          roomId={roomId}
          open={giftsEnabled && isGiftPickerOpen}
          onOpenChange={setIsGiftPickerOpen}
          onConfirm={handleConfirmGiftBets}
        />
      </>
    )
  }

  if (effectiveBetAsset === "GIFT") {
    return (
      <>
        <div className="relative flex flex-col gap-3 px-4 pb-3 pt-4">
          {assetToggle}
          {giftPresetRow}
          <GiftModeCta
            hasGifts={hasGifts}
            selectGiftsLabel={t("betting.selectGifts")}
            onSelectGifts={() => setIsGiftPickerOpen(true)}
          />
        </div>
        <GiftPickerSheet
          roomId={roomId}
          open={giftsEnabled && isGiftPickerOpen}
          onOpenChange={setIsGiftPickerOpen}
          onConfirm={handleConfirmGiftBets}
        />
      </>
    )
  }

  return (
    <>
      <div className="relative flex flex-col gap-3 px-4 pb-3 pt-4">
        {assetToggle}
        <div className="flex h-10 w-full items-center gap-2">
          <IconButton
            aria-label={t("betting.openBetSettings")}
            onClick={() => setSheetOpen(true)}
            variant={
              shouldHighlightSettings
                ? lockedMode
                  ? lockedMode === "solo"
                    ? "green"
                    : "orange"
                  : "green"
                : "default"
            }
            icon={
              <SettingsIcon
                className={cn(
                  "size-6",
                  shouldHighlightSettings
                    ? lockedMode
                      ? lockedMode === "solo"
                        ? "text-green-100"
                        : "text-[#FCD255]"
                      : "text-green-100"
                    : "text-white"
                )}
              />
            }
          />

          {settings.presets.map((preset, idx) => {
            const active = activePresetIndex === idx

            return (
              <PresetButton
                key={idx}
                presetNumber={idx + 1}
                variant={
                  active
                    ? lockedMode
                      ? lockedMode === "solo"
                        ? "green"
                        : "orange"
                      : "green"
                    : "default"
                }
                className="min-w-0 flex-1"
                onClick={() => applyAmount(preset)}
              >
                {formatPresetLabel(preset)}
              </PresetButton>
            )
          })}

          <PresetButton
            presetNumber={4}
            variant={allInButtonActive ? "green" : "default"}
            className="min-w-0 flex-1"
            disabled={allInValue <= 0}
            onClick={() => applyAmount(allInValue, { preservePrecision: true })}
          >
            {formatPresetLabel(allInValue)}
          </PresetButton>
        </div>

        {lockedMode ? (
          <LargeButton
            variant={lockedMode === "squad" ? "orange" : "green"}
            isLoading={isSubmitting || !!pending}
            disabled={!canBet}
            onClick={() => handlePlaceBet(lockedMode)}
          >
            Add
            <span>{amt}</span>
            <div className="relative flex size-[19px] shrink-0 items-center justify-center rounded-full bg-black">
              <TonIcon className="absolute left-1/2 top-0 size-5 -translate-x-1/2 -translate-y-px drop-shadow-[0_-1px_0_rgb(0,0,0)]" />
            </div>
          </LargeButton>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <ShortButton
              variant="green"
              aria-label={t("betting.ariaSoloBet")}
              isLoading={isSubmitting || !!pending}
              disabled={!canBet}
              onClick={() => handlePlaceBet("solo")}
            >
              {t("betting.solo")}
              <span>{amt}</span>
              <div className="relative flex size-[19px] shrink-0 items-center justify-center rounded-full bg-black">
                <TonIcon className="absolute left-1/2 top-0 size-5 -translate-x-1/2 -translate-y-px drop-shadow-[0_-1px_0_rgb(0,0,0)]" />
              </div>
            </ShortButton>
            <ShortButton
              variant="orange"
              aria-label={t("betting.ariaSquadBet")}
              isLoading={isSubmitting || !!pending}
              disabled={!canBet}
              onClick={() => handlePlaceBet("squad")}
            >
              {t("betting.squad")}
              <span>{amt}</span>
              <div className="relative flex size-[19px] shrink-0 items-center justify-center rounded-full bg-black">
                <TonIcon className="absolute left-1/2 top-0 size-5 -translate-x-1/2 -translate-y-px drop-shadow-[0_-1px_0_rgb(0,0,0)]" />
              </div>
            </ShortButton>
          </div>
        )}
      </div>

      <BetSettingsSheet
        roomId={roomId}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        balanceAmount={balanceData?.balance?.amount}
        onSettingsChange={setSettings}
        sectorNowPercent={Math.max(0, Math.round(sectorNowPercent))}
        sectorAfterPercent={Math.max(0, Math.round(sectorAfterPercent))}
        lockedBetMode={lockedMode}
        canPlaceBet={canBet}
        placeBetLoading={isSubmitting || !!pending}
        onPlaceFromSheet={handlePlaceFromSheet}
      />
    </>
  )
}

function appendDemoBetToSnapshot(
  snapshot: RoundSnapshot,
  params: {
    userId: string
    amountTon: number
    mode: "solo" | "squad"
    nowMs: number
  }
): RoundSnapshot {
  const { userId, amountTon, mode, nowMs } = params
  const userData = resolveBetUserData(snapshot, userId)
  const amount = amountTon.toFixed(2)
  const partyType = mode === "squad" ? "TEAM" : "SOLO"
  const partyId = mode === "squad" ? DEFAULT_GROUP_ID : `solo:${userId}`
  const groupId = mode === "squad" ? DEFAULT_GROUP_ID : undefined
  const nextBet: SnapshotBet = {
    betId: `demo-bet-${nowMs}-${Math.random().toString(36).slice(2, 8)}`,
    userData,
    amount,
    currency: "ton",
    status: "ACTIVE",
    partyId,
    partyType,
    groupId,
    createdAt: nowMs,
  }
  const nextBets = [...snapshot.bets, nextBet]
  const nextParties = rebuildPartiesFromBets(nextBets)
  const totalTon = nextParties.reduce(
    (sum, party) => sum + Number.parseFloat(party.betAmount || "0"),
    0
  )

  return {
    ...snapshot,
    version: snapshot.version + 1,
    bets: nextBets,
    parties: nextParties,
    prizePoolTon: totalTon.toFixed(2),
  }
}

function resolveBetUserData(snapshot: RoundSnapshot, userId: string): BetUserData {
  const fromBet = snapshot.bets.find((b) => b.userData.userId === userId)?.userData
  if (fromBet) return fromBet
  const fromParty = snapshot.parties.find((p) => p.userId === userId)
  if (fromParty) {
    return {
      userId,
      username: fromParty.username ?? userId,
      photoUrl: null,
    }
  }
  return { userId, username: userId, photoUrl: null }
}

function rebuildPartiesFromBets(bets: SnapshotBet[]): RoundParty[] {
  const activeBets = bets.filter((b) => b.status === "ACTIVE" || b.status === "PENDING")
  const total = activeBets.reduce(
    (sum, b) => sum + Number.parseFloat(b.amount || "0"),
    0
  )
  const byParty = new Map<
    string,
    {
      key: string
      partyType: "SOLO" | "TEAM"
      userId: string
      username: string
      amount: number
      users: Map<string, { userData: BetUserData; amount: number }>
    }
  >()

  for (const bet of activeBets) {
    const isTeam =
      bet.partyType === "TEAM" ||
      bet.partyId === DEFAULT_GROUP_ID ||
      bet.groupId === DEFAULT_GROUP_ID
    const key = isTeam ? DEFAULT_GROUP_ID : bet.partyId ?? `solo:${bet.userData.userId}`
    const party = byParty.get(key) ?? {
      key,
      partyType: isTeam ? "TEAM" : "SOLO",
      userId: isTeam ? DEFAULT_GROUP_ID : bet.userData.userId,
      username: isTeam ? "Squad" : bet.userData.username ?? bet.userData.userId,
      amount: 0,
      users: new Map(),
    }
    const amt = Number.parseFloat(bet.amount || "0")
    party.amount += amt
    const userEntry = party.users.get(bet.userData.userId) ?? {
      userData: bet.userData,
      amount: 0,
    }
    userEntry.amount += amt
    party.users.set(bet.userData.userId, userEntry)
    byParty.set(key, party)
  }

  return Array.from(byParty.values()).map((party) => {
    const percent = total > 0 ? ((party.amount / total) * 100).toFixed(2) : "0.00"
    if (party.partyType === "TEAM") {
      return {
        userId: DEFAULT_GROUP_ID,
        username: "Squad",
        betAmount: party.amount.toFixed(2),
        percent,
        partyId: DEFAULT_GROUP_ID,
        partyType: "TEAM",
        totalBank: Math.round(party.amount * 1e9),
        playerBanks: Array.from(party.users.values()).map((user) => ({
          userData: user.userData,
          bank: Math.round(user.amount * 1e9),
        })),
      }
    }
    return {
      userId: party.userId,
      username: party.username,
      betAmount: party.amount.toFixed(2),
      percent,
      partyId: party.key,
      partyType: "SOLO",
      totalBank: Math.round(party.amount * 1e9),
    }
  })
}

const resolveNextAmount = (params: {
  allInValue: number
  hasStoredSettings: boolean
  prevAmount: number
  loadedAmount: number
}): number => {
  const { allInValue, hasStoredSettings, prevAmount, loadedAmount } = params

  if (allInValue <= 0) return 0
  if (!hasStoredSettings) return Math.min(loadedAmount, allInValue)
  if (prevAmount > 0) return Math.min(prevAmount, allInValue)

  return loadedAmount
}

/**
 * GIFT-mode bet panel CTA.
 * - `hasGifts === undefined`  — cache not loaded yet → "Select gifts" in loading state.
 * - `hasGifts === true`       — wide green "Select gifts" CTA.
 * - `hasGifts === false`      — empty-state pair: gray "Deposit gift" + green "Buy gift" (marketplace tab).
 */
function GiftModeCta({
  hasGifts,
  selectGiftsLabel,
  onSelectGifts,
}: {
  hasGifts: boolean | undefined
  selectGiftsLabel: string
  onSelectGifts: () => void
}) {
  const { t } = useI18n()
  const navigate = useNavigate()

  const handleDepositGift = () => {
    telegram.openTelegramLink(getGiftRelayBotTelegramUrl())
  }

  const handleBuyGift = () => {
    navigate("/inventory?tab=marketplace")
  }

  if (hasGifts === undefined) {
    return <LargeButton variant="green" isLoading disabled />
  }

  if (hasGifts) {
    return (
      <LargeButton variant="green" onClick={onSelectGifts}>
        {selectGiftsLabel}
      </LargeButton>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      <ShortButton variant="default" onClick={handleDepositGift}>
        {t("betting.depositGift")}
      </ShortButton>
      <ShortButton variant="green" onClick={handleBuyGift}>
        {t("betting.buyGift")}
      </ShortButton>
    </div>
  )
}
